import React, { Component } from 'react';
import './account.scss';
import './accountContainer.scss';

import FlipCard, {FlipCardFront, FlipCardBack} from '../../../components/FlipCard/FlipCard';
import Withdrawal from './Withdrawal';
import Deposit from './Deposit';
import infoIcon from '../../../assets/images/info-icon.svg';
import chevDown from '../../../assets/images/chev-down-royal.svg';
import chevUp from '../../../assets/images/chev-up.svg';
import { formatDate, formatCurrency} from '../../../utils/helpers';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
import Button from '../../../components/Form/Button/Button';
import AccountActivity from '../AccountActivity/AccountActivity';
import '../../SharedComponents/button-styles.scss'
import AccountDetails from "./AccountDetails";
import NoticeAccountClosureModal from "../../MyAccount/AccountClosure/NoticeAccountClosureModal";
import RateChangeNotification from '../RateChangeNotification/RateChangeNotification';
import { useUnifiedAnalytics } from '../../../contexts/AnalyticsWrapper';

/** @import { WithFeatureFlagsProps } from '../../FeatureFlags/FeatureFlagsTypes' */
/** @import { AccountNSProps,AccountNSState } from './AccountNSTypes' */
/** @import { Account, PendingTransaction } from "../DashboardTypes" */
/** @import {DestinationAccount} from '../../MyAccount/MyAccountTypes' */

/**
 * @typedef {Object} WithAnalyticsProps
 * @property {import('../../../contexts/AnalyticsWrapperTypes').UnifiedAnalytics} analytics
 */

/** @extends {Component<AccountNSProps & WithFeatureFlagsProps & WithAnalyticsProps, AccountNSState>} */
class AccountNS extends React.Component {
    /** @type {AccountNSState} */
    state = {
        flipped: false,
        action: null,
        accountActivitiesEnabled: false,
        showClosure: false,
        selectedAccountNumber: '',
        showPopupInline: null,
        isRateNotificationVisible: false,
    };
    setRateChangeNotification = () => {
        const { account, analytics } = this.props;
                
        this.setState({
            isRateNotificationVisible: !this.state.isRateNotificationVisible
        });
        
        if(analytics){        
            analytics.trackAnalyticsClickEvent(
                'rate change',
                window.location.href,
                'rate notification',
                account?.product.name,
                '',
                {},
                this.state.isRateNotificationVisible ? 'hide' : 'reveal',
                'dashboard|click|show-rate-change',
                'dashboard'
            );
        }
    };

    toggleInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        if (!this.props.showPopup(event, () => this.renderBlendedRate())) {
            this.setState(({showPopupInline}) => ({
                showPopupInline: showPopupInline ? null : 'BLENDED_RATE'
            }))
        }
    };

    toggleBalanceInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        if (!this.props.showPopup(event, () => this.renderBalancePopup())) {
            this.setState(({showPopupInline}) => ({
                showPopupInline: showPopupInline ? null : 'AVAILABLE_BALANCE'
            }))
        }
    };

    toggleNoticeClosureInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        if (!this.props.showPopup(event, () => this.renderNoticeClosureInfo())) {
            this.setState(({ showPopupInline }) => ({
                showPopupInline: showPopupInline ? null : 'NOTICE_CLOSURE'
            }))
        }
    };

    onSelectActivity = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
        event.preventDefault();

        const {account, onSelectActivity} = this.props;

        this.setState(({accountActivitiesEnabled}) => ({
            accountActivitiesEnabled: !accountActivitiesEnabled
        }), () => {
            if (typeof onSelectActivity === 'function') onSelectActivity(account);
        });
    };

    startAction(/** @type {string} */ action) {

        this.setState({
            isRateNotificationVisible: !this.state.isRateNotificationVisible ? this.state.isRateNotificationVisible : !this.state.isRateNotificationVisible
        });

        this.setState({action}, () => {
            setTimeout(() => {
                this.setState({flipped: true}, () => {
                    this.onActionStart();
                });
            }, 1);
        });
    };

    startWithdrawal = () => {
        const { analytics } = this.props;
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'withdraw',
                window.location.href,
                'dashboard',
                'notice-saver',
                'ns withdrawal journey',
                {},
                'ns withdrawal',
                'dashboard|click|ns-move-money',
                'dashboard'
            );
        }
        this.startAction('withdraw');
    };

    startTransfer = () => {
        this.startAction('transfer');
    };

    startDeposit = () => {
        this.startAction('deposit');
    };

    closeAccount = (/** @type {string} */ accountNumber) => {
        this.setState(() => ({
            showClosure: true,
            selectedAccountNumber: accountNumber
        }));
        const { analytics } = this.props;
        if (analytics) {
            analytics.trackAnalyticsPopupClick(
                'Close my account',
                'view',
                'closing ns account',
                'Account Closure Feature',
                'popup|view|close my account'
            );
        }
    };

    unflipCard = () => {
        const {account, onActionComplete} = this.props;
        this.setState({flipped: false}, () => {
            if (typeof onActionComplete === 'function') onActionComplete(account);
        });
    };

    onActionStart = () => {
        const {account, onActionStart} = this.props;
        if (typeof onActionStart === 'function') onActionStart(account);
    };

    onAfterFlip = (/** @type {boolean} */ flipped) => {
        if (!flipped) {
            this.setState({action: null});
        }
    };

    onAccountClosed = () => {
        const {account} = this.props;

        account.availableBalance = 0;

        this.setState(() => ({
            showClosure: false
        }));
        this.unflipCard();
    }

    onAccountClosureCancelled = () => {
        this.setState(() => ({
            showClosure: false
        }));
    }

    /** @returns {DestinationAccount[]} */
    getDestinationAccounts = () => {
        let {account} = this.props;
        /** @type {DestinationAccount[]} */
        let mappedAccounts = account.transferAccounts.map(function (account) {
            return {
                accountNumber: account.accountNumber,
                sortCode: account.sortCode,
                productName: /** @type {string | null} */ (account.product.name),
                label: `${account.product.name} - ${account.accountNumber}`,
                productType: /** @type {string | null} */ (account.product.productType),
                balance: /** @type {number | null} */ (account.balance),
                availableBalance: /** @type {number | null} */ (account.availableBalance)
            };
        });

        mappedAccounts.push({
            accountNumber: account.linkedAccountNumber || '',
            sortCode: account.linkedAccountSortCode || '',
            productName: null,
            label: `Linked Account - ${account.linkedAccountNumber}`,
            productType: null,
            balance: null,
            availableBalance: null
        });

        return mappedAccounts;
    };

    getNsProfileData = () => {
        let {account, customer} = this.props;
        return {
            interestPaidTo: account.interestPaid,
            mobileNumber: customer.mobileNumber,
            accountId: account.id,
            accountStatus: account.accountStatus,
            accountNumber: account.accountNumber,
            productName: account.product.name,
            noticePeriod: account.notice ? account.notice.noticePeriod + ' ' + account.notice.noticePeriodUnit : '',
            destinationAccounts: this.getDestinationAccounts(),
            balance: account.balance,
            availableBalance: account.availableBalance
        };
    };

    accountIsClosed = (/** @type {string} */ status) => {
        return status === "ClosureRequested" || status === "Closed";
    };

    renderClosureMessage = (/** @type {string} */status) => {
        if (status === "ClosureRequested")
            return 'CLOSURE IN PROGRESS';

        if (status === "Closed")
            return "CLOSED";

        return '';
    };

    renderAccountInterestRate(/** @type {string} */ className) {
        const {account} = this.props;
        return (
            <div className={["account__interest-rate", className].join(' ')}>
                <span className="account__info-key">Interest rate</span>
                <span className="account__info-value">
                    <span>{account.product.interestRate.toFixed(2)} %</span>
                    <a href="#" onClick={this.toggleInfo}><img className="account__interest-rate-info" src={infoIcon}/></a>
                </span>
            </div>
        );
    };

    renderAccountLink(/** @type {string} */ className) {
        const {activityIsShowing} = this.props;
        let activeClass = activityIsShowing ? 'account__link--active' : '';
        return (
            <a href="#" className={["account__link", activeClass, className].join(' ')} onClick={this.onSelectActivity}>
                <span>Account activity</span>
                {this.state.accountActivitiesEnabled && <img className="account__link-icon" src={chevUp}/>}
                {!this.state.accountActivitiesEnabled && <img className="account__link-icon" src={chevDown}/>}
            </a>
        );
    };

    renderBlendedRate() {
        const {account} = this.props;
        return (
            <>
                <h3 className="account__blended-rate-title">What's the interest rate?</h3>
                <ul className="account__blended-rate-items">
                    {
                        account.product.interestRateTiers && account.product.interestRateTiers
                            .sort((a, b) => a.minBalance > b.minBalance ? -1 : 1)
                            .map((tier, index) => {
                                return tier.rate === 0.00
                                    ? (<li className="account__blended-rate-item" key={index}>However, if it's equal to or
                                        under {formatCurrency(tier.maxBalance)}, you won't earn any interest.</li>)
                                    : (<li className="account__blended-rate-item" key={index}>You'll
                                        earn {tier.rate.toFixed(2)}%^ on your <b>entire</b> balance when it's
                                        between {formatCurrency(tier.minBalance)} - {formatCurrency(tier.maxBalance)}.
                                    </li>)
                            })
                    }
                    <li className="account__blended-rate-item" key={account.product.interestRateTiers && account.product.interestRateTiers.length}>^This is
                        gross p.a., which is the rate you earn per year before tax.
                    </li>
                </ul>
            </>
        );
    };

    renderBalancePopup() {
        return (
            <>
                <h3 className="account__blended-tooltip-title">What's the available balance?</h3>
                <ul className="account__blended-tooltip-items">
                    <li className="account__blended-tooltip-item">This is the portion of your balance that isn't already scheduled for withdrawal.</li>
                </ul>
            </>
        );
    };

    getPendingTransactionTaggedForClosing = (/** @type {Account} */ acc) => {
        return acc.pendingTransactions.find(pt => pt.closeAccount);
    };

    renderInfoSection() {
        const { account } = this.props;
        const pendingTransaction = this.getPendingTransactionTaggedForClosing(account);
        if (pendingTransaction) {
            return this.renderNoticeClosureMessageSection(pendingTransaction);
        }
        else if (this.accountIsClosed(account.accountStatus)) {
            return this.renderClosureMessageSection();
        }
        else {
            return this.renderDefaultMessageSection();
        }
    };

    renderNoticeClosureMessageSection = (/** @type {PendingTransaction} */ pendingTransaction) => {
        return (
            <div className="account__actions">
                <div className="account__section-right-space">
                    <span className="account__info-key account__info-key-blue">Closes on</span>
                    <span className="account__info-value">
                        <a href="#" onClick={this.toggleNoticeClosureInfo}><img className="account__interest-account-info" src={infoIcon} /></a>
                    </span>
                    {this.state.showPopupInline === 'NOTICE_CLOSURE' && (
                        <div className="account__inline-popup is-open">
                            {this.renderNoticeClosureInfo()}
                        </div>
                    )}
                </div>
                <div className="account__section-right-space">
                    <span className="account__info-value">{formatDate(new Date(pendingTransaction.withdrawalDate))}</span>
                </div>
            </div>
        );
    };

    renderNoticeClosureInfo() {
        return (
            <>
                <h3 className="account__blended-tooltip-title">What does this mean?</h3>
                <ul className="account__blended-tooltip-items">
                    <li className="account__blended-tooltip-item">We'll automatically transfer your total balance, including your interest, back to your chosen account on this date.</li>
                </ul>
            </>
        );
    };

    renderClosureMessageSection() {
        const {account} = this.props;
        return (
            <div className="account__actions">
                <p className="account__closed-message">{this.renderClosureMessage(account.accountStatus)}</p>
            </div>
        );
    };

    renderDefaultMessageSection() {
        const { account, featureEnabled } = this.props;
        return (
            <div className="account__actions">
                <Button size='med'
                        label="Move money"
                        disabled={account.balance === 0 || account.availableBalance === 0 || !featureEnabled('CoreWithdrawalFunctionality')}
                        onClick={this.startWithdrawal} />
            </div>
        );
    };

    renderAccountFront() {
        const { account, featureEnabled, customer } = this.props;
        const rateChangeslist = account.product.rateChanges;
        return (
            <div className="accountcontainer">
                <div
                    className={this.accountIsClosed(account.accountStatus) ? "account__front account__front--closed" : "account__front"}>
                    <AccountDetails showPopup={this.props.showPopup}
                                    account={account}
                                    issueNumber={account.product.issueNumber}
                                    customer={customer}
                                    enableBlendedRatePopup/>
                    {/*<div className="account__divider"/>*/}
                    <div
                        className="account__section-right">
                        <div className="account__balance">
                            <span className="account__balance-key">Balance</span>
                            <span className="account__balance-value"
                                  data-accountbalance-number={account.accountNumber}>{formatCurrency(account.balance)}</span>
                        </div>
                        {account.balance !== account.availableBalance && (
                            <div className="account__balance">
                                <div className="account__available-balance-container">
                                    <span
                                        className="account__available-balance account__info-value">{formatCurrency(account.availableBalance)} available</span>
                                    <span className="account__info-value">
                                        <a onClick={this.toggleBalanceInfo}>
                                            <img className="account__available-balance-info" src={infoIcon}/>
                                        </a>
                                    </span>
                                    {this.state.showPopupInline === 'AVAILABLE_BALANCE' && (
                                        <div className="account__inline-popup is-open">
                                            {this.renderBalancePopup()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {this.renderAccountLink('account__link--desktop')}
                    </div>
                    {/*<div className="account__divider"/>*/}
                    {this.renderInfoSection()}
                    {this.renderAccountLink('account__link--mobile')}
                </div>
                <div className={this.accountIsClosed(account.accountStatus) ? "accountcontainer__infocontainerhide" : (this.state.flipped ? "accountcontainer__infocontainerhide" : "accountcontainer__front accountcontainer__front--closed")}>

                    {featureEnabled('EnableInterimRateDecrease') ? (rateChangeslist != null && rateChangeslist.length > 0 &&
                        (<div className="accountcontainer__section-left"><RateChangeNotification
                            isRateNotificationVisible={this.state.isRateNotificationVisible}
                            flipped={this.state.flipped} setRateChangeNotification={this.setRateChangeNotification}
                            rateChangeList={rateChangeslist}>
                        </RateChangeNotification></div>)) : <></>}
                </div>
            </div>
        );
    };

    renderWithdrawal() {
        const {account, toAccount, customer} = this.props;
        return (
            <Withdrawal
                account={account}
                toAccount={toAccount}
                customer={customer}
                onCancel={this.unflipCard}
                onDone={this.unflipCard}
                onCloseAccount={this.closeAccount}
                enableClosure={true}
                noticePeriod={{
                    unit: account.notice?.noticePeriodUnit ?? undefined,
                    length: account.notice?.noticePeriod ?? undefined
                }}/>
        );
    };

    renderDeposit() {
        const {account, toAccount} = this.props;
        return (
            <Deposit
                account={account}
                toAccount={toAccount}
                onCancel={this.unflipCard}
                onDone={this.unflipCard}/>
        );
    };

    renderAccountBackWithdrawal() {
        const {action} = this.state;
        if (action === 'deposit') return this.renderDeposit();
        if (action) return this.renderWithdrawal();
        return null;
    };

    getClassNames() {
        const {hide, disable} = this.props;
        return ['account', hide ? 'account--hidden' : '', disable ? 'account--disabled' : ''].join(' ');
    };

    render() {
        const {flipped} = this.state;
        const {account, accountWithActionInProgress} = this.props;
        const profileData = this.getNsProfileData();

        return (
            <>
                <div className={this.getClassNames()}>
                    <FlipCard flipped={flipped} onAfterFlip={this.onAfterFlip}>
                        <FlipCardFront>{this.renderAccountFront()}</FlipCardFront>
                        <FlipCardBack>{this.renderAccountBackWithdrawal()}</FlipCardBack>
                    </FlipCard>
                    <div>
                        {this.state.showClosure && <NoticeAccountClosureModal
                            productName={profileData.productName}
                            mobileNumber={profileData.mobileNumber}
                            accountId={profileData.accountId}
                            accountStatus={profileData.accountStatus}
                            selectedAccountNumber={this.state.selectedAccountNumber}
                            destinationAccounts={profileData.destinationAccounts}
                            onClosed={this.onAccountClosed}
                            onCancel={this.onAccountClosureCancelled}
                        />}
                    </div>
                </div>
                {this.state.accountActivitiesEnabled &&
                    <AccountActivity activityRef={this.props.activityRef} account={account}
                                     disable={Boolean(accountWithActionInProgress)}/>}
            </>
        );
    };
};

/**
 * Higher-order component that injects analytics
 * @param {React.ComponentType<any>} Component - Component that needs analytics
 * @returns {React.ComponentType<any>} - Wrapped component with analytics injected
 */
const WithAnalytics = (Component) => {
    /**
     * @param {Object} props - Component props without analytics
     * @returns {JSX.Element}
     */
    return function WrappedComponent(props) {
        const analytics = useUnifiedAnalytics();
        return <Component {...props} analytics={analytics} />;
    }
};

export default WithAnalytics(withFeatureFlags(AccountNS));
